export interface FormNavState<PageEnum extends string> {
    activePage: PageEnum,
    previousPages: PageEnum[],
    errors: string[],
}

export interface Section<PageEnum extends string> {
    name: string, pages: PageEnum[]
}

export interface PageSpec<PageEnum extends string> {
    onLoad?: ()=>void,
    validate?: ()=>string[]|undefined,
    enabled?: ()=>boolean,
    nextPage?: PageEnum|(()=>PageEnum),
    disallowBack?: boolean,
}

export type PageSpecs<PageEnum extends string> = {
    [key in PageEnum]: PageSpec<PageEnum>
}

export interface Form<PageEnum extends string> {
    state: FormNavState<PageEnum>,

    determineCurrentSection: ()=>{name:string, progress:number}|null,

    canNavigateForward: ()=>boolean,
    navigateForward: ()=>void,

    canNavigateBack: ()=>boolean,
    navigateBack: ()=>void,

    displayPage: (page: PageEnum, resetHistory?: boolean)=>void
}

export function useForm<PageEnum extends string>(
    pages: PageSpecs<PageEnum>,
    sections: Section<PageEnum>[],
    state: FormNavState<PageEnum>,
    onChange: ()=>any
): Form<PageEnum> {
    const displayPage = (page: PageEnum, resetHistory?: boolean) => {
        if (resetHistory) {
            state.previousPages = [];
        }
        
        const pageToDisplay = pages[page];
        if (pageToDisplay.onLoad) {
            pageToDisplay.onLoad();
        }
    
        state.activePage = page;
        state.errors = [];
        window.scrollTo(0,0);
    };

    displayPage(state.activePage);
    
    return {
        state: state,

        determineCurrentSection: () => {
            let pageCount = 0;
            let activePageIndex = null;
            let activeSectionName = null;
            for (const section of sections) {
                for (const page of section.pages) {
                    if (page == state.activePage) {
                        activePageIndex = pageCount;
                        activeSectionName = section.name;
                    }
                    ++pageCount;
                }
            }

            return activePageIndex == null ? null : {name: activeSectionName!, progress: activePageIndex / (pageCount-1)};
        },

        canNavigateForward: () => {
            return pages[state.activePage].nextPage != undefined
        },
    
        navigateForward: () => {
            const currentPage = pages[state.activePage];
            
            // If any validation logic has been specified, apply it
            if (currentPage.validate) {
                state.errors = currentPage.validate() || [];
                if (state.errors.length > 0) {
                    onChange();
                    return;
                }
            }
        
            // Determine what the next page should be
            let pageId = state.activePage;
            let page = currentPage;

            while (pageId) {
                // Keep looking up the next page, skipping over any disabled pages
                pageId = (typeof page.nextPage == "function") ? page.nextPage() : page.nextPage!;
                
                page = pages[pageId];
                if (page.enabled == undefined || page.enabled() == true) break;
            }
            
            state.previousPages.push(state.activePage);
            displayPage(pageId);
            onChange();
        },

        canNavigateBack: () => {
            return state.previousPages.length > 0 && !pages[state.activePage].disallowBack;
        },

        navigateBack: () => {            
            const previousPage = state.previousPages.pop();
            if (previousPage) {
                displayPage(previousPage);
                onChange();
            }
        },

        displayPage: displayPage
    }
}